<template>
	<section>
		<editor-menu-bar
			class="editor-menu-bar"
			:editor="editor"
			v-slot="{ commands, isActive, focused }"
		>
			<div class="menubar is-hidden" :class="{ 'is-focused': focused }">
				<div class="b360-label-holder">
					<span class="b360-form-label">{{ labelName }}</span>
				</div>

				<div class="menubar-holder">
					<button
						class="menubar__button"
						:class="{ 'is-active': isActive.bold() }"
						@click="commands.bold"
					>
						<font-awesome-icon :icon="['fas', 'bold']" />
					</button>
					<button
						class="menubar__button"
						:class="{ 'is-active': isActive.italic() }"
						@click="commands.italic"
					>
						<font-awesome-icon :icon="['far', 'italic']" />
					</button>
					<button
						class="menubar__button"
						:class="{ 'is-active': isActive.underline() }"
						@click="commands.underline"
					>
						<font-awesome-icon :icon="['far', 'underline']" />
					</button>
					<button
						class="menubar__button"
						:class="{ 'is-active': isActive.bullet_list() }"
						@click="commands.bullet_list"
					>
						<font-awesome-icon :icon="['far', 'list']" />
					</button>
					<button
						class="menubar__button"
						:class="{ 'is-active': isActive.ordered_list() }"
						@click="commands.ordered_list"
					>
						<font-awesome-icon :icon="['far', 'list-ol']" />
					</button>
				</div>
			</div>
		</editor-menu-bar>
		<editor-content
			ref="editorContent"
			class="editor-content"
			:editor="editor"
			:control="control"
		/>
	</section>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap';
import {
	Bold,
	Italic,
	Underline,
	BulletList,
	ListItem,
	OrderedList,
} from 'tiptap-extensions';

export default {
	name: 'RichText',
	props: {
		variationIndex: Number,
		control: Object,
		labelName: String,
		fieldName: String,
	},
	components: {
		// eslint-disable-next-line vue/no-unused-components
		Editor,
		EditorContent,
		EditorMenuBar,
	},
	data() {
		return {
			editor: null,
		};
	},
	watch: {
		variationIndex() {
			// Swap this component's text when ever the variation changes
			this.editor.setContent(this.control.value);
		},
	},
	mounted() {
		this.editor = new Editor({
			extensions: [
				new Bold(),
				new Italic(),
				new Underline(),
				new BulletList(),
				new ListItem(),
				new OrderedList(),
			],
			content:
				typeof this.control.value !== 'undefined' ? this.control.value : '',
			onUpdate: ({ getHTML }) => {
				const updatedHtml = getHTML();
				/* NOTE: Updated text is used for browser refresh and
					in some cases for the variation select dropdown options */
				const updatedText = this.$refs.editorContent.$el.innerText;
				const data = {
					value: updatedHtml,
					fieldName: this.fieldName,
					index: this.variationIndex,
					updatedText,
				};
				this.$parent.updateTextControl(data);
			},
			onFocus: () => {
				/*
				 * The timeout below gives the tiptap editor some time to set it's
				 * "selection" or cursor position. If we don't wait, the selection
				 * may be lost if the parent component removes focus and then later
				 * tries to set the focus back on the editor.
				 */
				setTimeout(() => {
					this.$emit('focus', {
						control: this.control,
					});
				}, 10);
			},
			onBlur: () => {
				this.$emit('blur', {
					control: this.control,
				});
			},
		});
	},
};
</script>

<style lang="scss" scoped>
.menubar.editor-menu-bar {
	position: relative;

	.menubar-holder {
		display: flex;
		position: absolute;
		@include rem(top, 44px);
		@include rem(left, 9px);
	}
}

/deep/ .ProseMirror {
	outline: none;
}

.menubar__button {
	justify-content: center;
	align-items: center;
	@include rem(width, 32px);
	@include rem(height, 32px);
	border-radius: $b360-border-radius-base;
	box-shadow: inset 0 0 0 $b360-border-width-thin $border-color-light;
	background-color: transparent;
	border: unset;
	color: $subheader-color;
	cursor: pointer;
	@include rem(margin, 0 4px);

	&.is-active,
	&:hover {
		background-color: $highlight-color__teal;
		box-shadow: inset 0 0 0 $b360-border-width-thin $brand-teal;
		color: $brand-teal;
	}
}

.menubar__button {
	display: flex;
}

.editor-content {
	@include rem(margin-bottom, 20px);
	border: $b360-border-width-thin solid $border-color-light;
	border-radius: $b360-border-radius-base;
	@include rem(padding, 42px 12px 0 12px);

	&:focus-within {
		box-shadow: 0 0 6px rgba(0, 0, 0, 0.1);
	}
}
</style>
